import { message } from 'antd'
import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import useMixpanelEvent, {
  MixpanelEvents
} from '../../../../../hooks/useMixpanelEvent'
import type { ServiceRecord } from '../types'

interface UseServiceRecordArchiveReturn {
  archiveServiceRecord: (serviceRecordId: string) => Promise<ServiceRecord>
  unarchiveServiceRecord: (serviceRecordId: string) => Promise<ServiceRecord>
  isArchiving: boolean
}

export const useServiceRecordArchive = (
  residentId: string,
  onSuccess?: () => void
): UseServiceRecordArchiveReturn => {
  const queryClient = useQueryClient()
  const sendMixpanelEvent = useMixpanelEvent()

  const archiveMutation = useMutation(
    async ({
      serviceRecordId,
      archive
    }: {
      serviceRecordId: string
      archive: boolean
    }) => {
      const response = await postJSON({
        url: `/v6/residents/service-records/${serviceRecordId}/archive`,
        method: 'PUT',
        json: { isArchived: archive }
      })

      return response.data as ServiceRecord
    },
    {
      onSuccess: (_, variables) => {
        sendMixpanelEvent(
          variables.archive
            ? MixpanelEvents.SERVICE_RECORD_ARCHIVED
            : MixpanelEvents.SERVICE_RECORD_UNARCHIVED,
          {
            residentId,
            serviceRecordId: variables.serviceRecordId
          }
        )
        message.success(
          variables.archive
            ? 'Service record archived successfully'
            : 'Service record restored successfully'
        )
        queryClient.invalidateQueries(['service-records', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update service record')
      }
    }
  )

  const archiveServiceRecord = async (
    serviceRecordId: string
  ): Promise<ServiceRecord> => {
    return archiveMutation.mutateAsync({ serviceRecordId, archive: true })
  }

  const unarchiveServiceRecord = async (
    serviceRecordId: string
  ): Promise<ServiceRecord> => {
    return archiveMutation.mutateAsync({ serviceRecordId, archive: false })
  }

  return {
    archiveServiceRecord,
    unarchiveServiceRecord,
    isArchiving: archiveMutation.isLoading
  }
}
